import * as THREE from 'three'

/**
 * 屏幕多图栅格过渡：在 `MeshStandardMaterial` 上链式叠加 `onBeforeCompile`，
 * 注入第二张目标贴图与混合权重 uniform，仅替换 `<map_fragment>` / `<emissivemap_fragment>` 取样段，
 * **完整保留** PBR 光照（含 IBL diffuse-only patch）、`emissiveIntensity` 与材质开关，
 * 因此过渡进行中、完成后与静态绑定下的色调一致。
 */

const PATCH_KEY = '__imockupScreenTransitionPatch'

export type ScreenTransitionUniforms = {
  uTransitionMapTo: { value: THREE.Texture | null }
  uTransitionMix: { value: number }
  /** 0 fade / 1 left / 2 right / 3 up / 4 down / 5 zoom / 6 circle */
  uTransitionMode: { value: number }
}

const TRANSITION_HEADER = /* glsl */ `
uniform sampler2D uTransitionMapTo;
uniform float uTransitionMix;
uniform int uTransitionMode;

float imockupTransEaseInOutCubic(float x) {
  x = clamp(x, 0.0, 1.0);
  return x < 0.5 ? 4.0 * x * x * x : 1.0 - pow(-2.0 * x + 2.0, 3.0) * 0.5;
}

vec4 imockupSampleTransitionedMap(sampler2D mapFromSampler, vec2 uv) {
  float tLin = clamp(uTransitionMix, 0.0, 1.0);
  if (tLin < 0.00001) {
    return texture2D(mapFromSampler, uv);
  }
  float u = imockupTransEaseInOutCubic(tLin);
  float uSlide = tLin;
  if (uTransitionMode == 0) {
    return mix(texture2D(mapFromSampler, uv), texture2D(uTransitionMapTo, uv), u);
  } else if (uTransitionMode == 1) {
    if (uv.x < 1.0 - uSlide) {
      return texture2D(mapFromSampler, vec2(uv.x + uSlide, uv.y));
    }
    return texture2D(uTransitionMapTo, vec2(uv.x + uSlide - 1.0, uv.y));
  } else if (uTransitionMode == 2) {
    if (uv.x < uSlide) {
      return texture2D(uTransitionMapTo, vec2(uv.x + (1.0 - uSlide), uv.y));
    }
    return texture2D(mapFromSampler, vec2(uv.x - uSlide, uv.y));
  } else if (uTransitionMode == 3) {
    if (uv.y < 1.0 - uSlide) {
      return texture2D(mapFromSampler, vec2(uv.x, uv.y + uSlide));
    }
    return texture2D(uTransitionMapTo, vec2(uv.x, uv.y + uSlide - 1.0));
  } else if (uTransitionMode == 4) {
    if (uv.y < uSlide) {
      return texture2D(uTransitionMapTo, vec2(uv.x, uv.y + (1.0 - uSlide)));
    }
    return texture2D(mapFromSampler, vec2(uv.x, uv.y - uSlide));
  } else if (uTransitionMode == 5) {
    float z = mix(0.22, 1.0, u);
    vec2 uvt = (uv - 0.5) / max(z, 0.0001) + 0.5;
    vec4 c0 = texture2D(mapFromSampler, uv);
    vec4 c1 = texture2D(uTransitionMapTo, clamp(uvt, 0.0001, 0.9999));
    return mix(c0, c1, u);
  } else if (uTransitionMode == 6) {
    float r = distance(uv, vec2(0.5)) * 2.0;
    float m = u * 2.0;
    float edge = smoothstep(m - 0.03, m + 0.03, r);
    vec4 c0 = texture2D(mapFromSampler, uv);
    vec4 c1 = texture2D(uTransitionMapTo, uv);
    return mix(c1, c0, edge);
  }
  return mix(texture2D(mapFromSampler, uv), texture2D(uTransitionMapTo, uv), u);
}
`

/**
 * 替换 three.js `<map_fragment>` chunk：用 helper 取样代替 `texture2D(map, vMapUv)`。
 * 与 r183 `map_fragment.glsl.js` 行为一致（包含 `DECODE_VIDEO_TEXTURE` 分支）。
 */
const MAP_FRAGMENT_REPLACEMENT = /* glsl */ `
#ifdef USE_MAP

	vec4 sampledDiffuseColor = imockupSampleTransitionedMap( map, vMapUv );

	#ifdef DECODE_VIDEO_TEXTURE

		sampledDiffuseColor = sRGBTransferEOTF( sampledDiffuseColor );

	#endif

	diffuseColor *= sampledDiffuseColor;

#endif
`

/**
 * 替换 `<emissivemap_fragment>` chunk：用 helper 取样代替 `texture2D(emissiveMap, vEmissiveMapUv)`。
 * 与 r183 `emissivemap_fragment.glsl.js` 行为一致。
 */
const EMISSIVEMAP_FRAGMENT_REPLACEMENT = /* glsl */ `
#ifdef USE_EMISSIVEMAP

	vec4 emissiveColor = imockupSampleTransitionedMap( emissiveMap, vEmissiveMapUv );

	#ifdef DECODE_VIDEO_TEXTURE_EMISSIVE

		emissiveColor = sRGBTransferEOTF( emissiveColor );

	#endif

	totalEmissiveRadiance *= emissiveColor.rgb;

#endif
`

/**
 * 为单个 `MeshStandardMaterial` 注入 / 复用过渡 uniform 与 fragment chunk 替换。
 * 同一材质幂等：第二次调用直接返回已存在的 uniform 句柄。
 *
 * @param material - 屏幕材质（非标准材质忽略并返回 null）
 */
export function attachScreenTransitionPatch(material: THREE.Material): ScreenTransitionUniforms | null {
  if (!(material instanceof THREE.MeshStandardMaterial)) return null
  const ud = material.userData as Record<string, unknown>
  const exist = ud[PATCH_KEY] as ScreenTransitionUniforms | undefined
  if (exist) return exist

  const uniforms: ScreenTransitionUniforms = {
    uTransitionMapTo: { value: null },
    uTransitionMix: { value: 0 },
    uTransitionMode: { value: 0 },
  }
  ud[PATCH_KEY] = uniforms

  const previous = material.onBeforeCompile
  material.onBeforeCompile = (shader, renderer) => {
    previous?.(shader, renderer)
    shader.uniforms.uTransitionMapTo = uniforms.uTransitionMapTo
    shader.uniforms.uTransitionMix = uniforms.uTransitionMix
    shader.uniforms.uTransitionMode = uniforms.uTransitionMode
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', `${TRANSITION_HEADER}\nvoid main() {`)
      .replace('#include <map_fragment>', MAP_FRAGMENT_REPLACEMENT)
      .replace('#include <emissivemap_fragment>', EMISSIVEMAP_FRAGMENT_REPLACEMENT)
  }
  material.needsUpdate = true
  return uniforms
}

/**
 * 为屏幕 mesh 上每个标准材质槽确保 patch 已挂载，返回所有 uniform 句柄（顺序与材质槽一致）。
 *
 * @param mesh - 屏幕网格
 */
export function ensureScreenTransitionPatchOnMesh(mesh: THREE.Mesh): ScreenTransitionUniforms[] {
  const mats: THREE.Material[] = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  const result: ScreenTransitionUniforms[] = []
  for (const m of mats) {
    const u = attachScreenTransitionPatch(m)
    if (u) result.push(u)
  }
  return result
}

/**
 * 写入一组 `(mapTo, mix, mode)` 到 mesh 上所有已挂 patch 的标准材质。
 * 写入 `null` 的 mapTo 会同步把 mix 归零（避免 sampler2D 取空纹理）。
 *
 * @param mesh - 屏幕网格
 * @param mapTo - 目标贴图（过渡结束后置 null）
 * @param mix - 0~1 的混合权重
 * @param mode - 过渡模式编号（0=fade、1~4=slide、5=zoom、6=circle）
 */
export function writeScreenTransitionUniforms(
  mesh: THREE.Mesh,
  mapTo: THREE.Texture | null,
  mix: number,
  mode: number,
): void {
  const mats: THREE.Material[] = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  for (const m of mats) {
    const u = (m.userData as Record<string, unknown>)[PATCH_KEY] as ScreenTransitionUniforms | undefined
    if (!u) continue
    u.uTransitionMapTo.value = mapTo
    u.uTransitionMix.value = mapTo ? Math.min(1, Math.max(0, mix)) : 0
    u.uTransitionMode.value = Math.max(0, Math.floor(mode))
  }
}

/**
 * 将 mesh 上所有已挂 patch 的过渡 uniform 还原为「无过渡」状态。
 *
 * @param mesh - 屏幕网格
 */
export function resetScreenTransitionUniforms(mesh: THREE.Mesh): void {
  writeScreenTransitionUniforms(mesh, null, 0, 0)
}
